import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowUpRight, ChevronDown, FileText, GitBranch, History } from 'lucide-react'
import { useState } from 'react'
import type { MemoryCopy, MemoryDerivationHistoryEntry, MemoryDetailSource } from './types'
import { formatMemoryDate } from './utils'

interface Props {
  copy: MemoryCopy
  currentText: string
  sources: MemoryDetailSource[]
  sourceCount: number
  truncated: boolean
  history: MemoryDerivationHistoryEntry[]
  onSelectDocument: (documentId: string) => void
}

export default function MemoryDerivation({ copy, currentText, sources, sourceCount, truncated, history, onSelectDocument }: Props) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const documentCount = new Set(sources.map((source) => source.document_id).filter(Boolean)).size
  const detailCopy = copy.detail

  return (
    <section data-testid="memory-derivation" className="space-y-3 border-t border-border pt-5">
      <header className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <GitBranch className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-foreground">{detailCopy.derivation_title}</h4>
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
            {replaceCount(sourceCount === 1 ? detailCopy.derivation_summary_one : detailCopy.derivation_summary, sourceCount)}
            {documentCount > 0 && (
              <span> · {replaceCount(documentCount === 1 ? detailCopy.derivation_documents_one : detailCopy.derivation_documents, documentCount)}</span>
            )}
          </p>
        </div>
      </header>

      <div className="space-y-2">
        {sources.map((source, index) => (
          <SourceFact key={source.id || index} source={source} copy={copy} onSelectDocument={onSelectDocument} />
        ))}
      </div>

      {truncated && (
        <p className="text-[11px] text-faint-foreground">
          {detailCopy.derivation_truncated.replace('%{shown}', String(sources.length)).replace('%{total}', String(sourceCount))}
        </p>
      )}

      {history.length > 0 && (
        <Collapsible data-testid="memory-derivation-history" open={historyOpen} onOpenChange={setHistoryOpen} className="rounded-lg border border-border bg-muted/20">
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" className="h-auto w-full justify-start gap-3 rounded-lg px-3 py-2.5 text-left">
              <History className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-foreground">{detailCopy.derivation_history}</span>
                <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">{detailCopy.derivation_history_description}</span>
              </span>
              <span className="text-[11px] font-normal text-muted-foreground">
                {historyOpen ? detailCopy.derivation_hide_history : detailCopy.derivation_show_history}
              </span>
              <ChevronDown className={`size-3.5 shrink-0 text-muted-foreground transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="divide-y divide-border border-t border-border">
              {history.map((entry, index) => (
                <HistoryEntry
                  key={`${entry.changed_at || 'history'}-${index}`}
                  entry={entry}
                  afterText={index === 0 ? currentText : history[index - 1]?.previous_text || currentText}
                  copy={copy}
                  onSelectDocument={onSelectDocument}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  )
}

function SourceFact({ source, copy, onSelectDocument }: { source: MemoryDetailSource; copy: MemoryCopy; onSelectDocument: (documentId: string) => void }) {
  return (
    <article className="rounded-lg border border-border/80 bg-background px-3 py-2.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* Sem o `context` da memória de origem: é metadado de ingestão do
              transcript, não conteúdo — mesmo motivo do painel de detalhe. */}
          <p className="text-xs leading-5 text-foreground/85">{source.text}</p>
        </div>
        {source.document_id && (
          <DocumentLink
            label={copy.detail.view_source}
            shortLabel={copy.detail.document_action}
            documentId={source.document_id}
            onSelect={onSelectDocument}
          />
        )}
      </div>
    </article>
  )
}

function HistoryEntry({ entry, afterText, copy, onSelectDocument }: { entry: MemoryDerivationHistoryEntry; afterText: string; copy: MemoryCopy; onSelectDocument: (documentId: string) => void }) {
  const detailCopy = copy.detail
  const evidence = entry.source_memories.filter((source) => source.is_new)
  const visibleEvidence = evidence.length > 0 ? evidence : entry.source_memories
  return (
    <article className="space-y-3 px-3 py-3">
      {entry.changed_at && <p className="text-[11px] font-medium text-muted-foreground">{detailCopy.derivation_changed_at.replace('%{date}', formatMemoryDate(entry.changed_at))}</p>}
      {entry.previous_text && (
        <div className="grid gap-2 sm:grid-cols-2">
          <HistoryText label={detailCopy.derivation_before} text={entry.previous_text} muted />
          <HistoryText label={detailCopy.derivation_after} text={afterText} />
        </div>
      )}
      {visibleEvidence.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10.5px] font-semibold text-faint-foreground">{detailCopy.derivation_evidence}</p>
          {visibleEvidence.map((source, index) => (
            <div key={source.id || index} className="flex items-start gap-2 rounded-md bg-background/80 px-2.5 py-2">
              <p className="min-w-0 flex-1 text-[11px] leading-4 text-muted-foreground">{source.text}</p>
              {source.document_id && (
                <DocumentLink
                  label={detailCopy.view_source}
                  shortLabel={detailCopy.document_action}
                  documentId={source.document_id}
                  onSelect={onSelectDocument}
                  compact
                />
              )}
            </div>
          ))}
        </div>
      )}
    </article>
  )
}

function HistoryText({ label, text, muted = false }: { label: string; text: string; muted?: boolean }) {
  return (
    <div className="rounded-md bg-background/80 p-2.5">
      <p className="text-[10px] font-semibold text-faint-foreground">{label}</p>
      <p className={`mt-1 line-clamp-4 text-[11px] leading-4 ${muted ? 'text-muted-foreground' : 'text-foreground/85'}`}>{text}</p>
    </div>
  )
}

function DocumentLink({ label, shortLabel, documentId, onSelect, compact = false }: { label: string; shortLabel: string; documentId: string; onSelect: (documentId: string) => void; compact?: boolean }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={compact ? 'h-7 shrink-0 gap-1 px-2 text-[10.5px] text-primary' : 'h-8 shrink-0 gap-1.5 px-2.5 text-[11px] text-primary'}
          onClick={() => onSelect(documentId)}
          aria-label={label}
        >
          <FileText className={compact ? 'size-3' : 'size-3.5'} />
          {shortLabel}
          <ArrowUpRight className="size-3" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={6}>{label}</TooltipContent>
    </Tooltip>
  )
}

function replaceCount(template: string, count: number) {
  return template.replace('%{count}', String(count))
}
